"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Network() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const headingLine1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const headingLine2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const manifestoBody = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pullQuote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const watermark = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const locations = [{
    city: "Sharjah",
    address: "Industrial Area 15, Warehouse Block B"
  }, {
    city: "Ajman",
    address: "Al Jurf Industrial 2, Logistics Hub"
  }, {
    city: "Al Ain",
    address: "Al Maqam Industrial Zone, Unit 04"
  }];
  return <section data-section-id="4809" id="the-world-sky-network" className="bg-slate-950 text-foreground relative overflow-hidden py-24 md:py-36 lg:py-48">
      <div ref={watermark.ref} aria-hidden="true" className={`pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/4 select-none font-default font-medium leading-none text-white transition-all duration-[1600ms] ease-out ${watermark.isVisible ? "opacity-100" : "opacity-0"}`} style={{
      fontSize: "clamp(8rem, 22vw, 20rem)"
    }}>
        02
      </div>

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={eyebrow.ref} className={`mb-20 flex justify-center transition-all duration-1000 ease-out md:mb-28 ${eyebrow.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-primary" style={{
          fontFamily: "var(--font-mono)"
        }}>
            Chapter II — Our Infrastructure
          </span>
        </div>

        <div className="grid grid-cols-12 gap-y-16 md:gap-x-8 lg:gap-x-12">
          <div className="col-span-12 md:col-span-6 md:col-start-1 lg:col-span-5 lg:col-start-2">
            <h2 className="font-default font-bold leading-[0.95] tracking-tighter text-white" style={{
            fontSize: "clamp(2.75rem, 7vw, 6rem)"
          }}>
              <span className="block overflow-hidden">
                <span ref={headingLine1.ref} className={`block transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${headingLine1.isVisible ? "translate-y-0" : "translate-y-full"}`}>
                  Global reach.
                </span>
              </span>
              <span className="block overflow-hidden">
                <span ref={headingLine2.ref} className={`block pl-[0.6em] italic transition-transform delay-150 duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${headingLine2.isVisible ? "translate-y-0" : "translate-y-full"}`}>
                  Local precision.
                </span>
              </span>
            </h2>
          </div>

          <div className="col-span-12 md:col-span-5 md:col-start-8 lg:col-span-4 lg:col-start-9">
            <div ref={manifestoBody.ref} className={`space-y-8 font-default text-lg leading-relaxed text-foreground transition-all duration-1000 ease-out md:text-xl ${manifestoBody.isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              <p className="text-primary-foreground">
                World Sky Building Materials Trading LLC bridges the gap between global manufacturing excellence and the dynamic construction landscape of the UAE.
              </p>

              <div className="flex items-center" aria-hidden="true">
                <span className="h-px w-12 bg-secondary" />
              </div>

              <p className="text-primary-foreground">
                We maintain direct partnerships with premium manufacturers in China, ensuring our aluminum profiles and architectural hardware meet the highest international standards for durability and finish.
              </p>

              <div className="flex items-center" aria-hidden="true">
                <span className="h-px w-12 bg-secondary" />
              </div>

              <div className="space-y-4">
                <p className="text-sm font-mono uppercase tracking-widest text-primary-foreground">Strategic Logistics Hubs:</p>
                <ul className="space-y-3 font-mono text-sm">
                  {locations.map((loc, i) => <li key={i} data-index={i} className="flex flex-col border-l border-primary/30 pl-4">
                      <span className="font-bold text-sky-400 uppercase tracking-wider">{loc.city}</span>
                      <span className="text-primary-foreground">{loc.address}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-28 grid grid-cols-12 gap-y-16 md:mt-40 md:gap-x-8 lg:gap-x-12">
          <div className="col-span-12 md:col-span-5 lg:col-span-4">
            <div ref={imageReveal.ref} className={`overflow-hidden transition-all duration-[1400ms] ease-out ${imageReveal.isVisible ? "opacity-100 [clip-path:inset(0%_0_0_0)]" : "opacity-90 [clip-path:inset(100%_0_0_0)]"}`}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profile_warehouse_stock.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profile_warehouse_stock.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722809/img/aluminum_profile_warehouse_stock.png-wvc-sizes" alt="Rows of premium aluminum profiles stored in our UAE warehouse facility" loading="lazy" decoding="async" className="aspect-[4/5] w-full object-cover rounded-lg" />
            </div>
          </div>

          <div className="col-span-12 flex items-center md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
            <div ref={pullQuote.ref} className={`transition-all duration-[1200ms] ease-out ${pullQuote.isVisible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}>
              <blockquote className="font-default italic leading-[1.15] text-white" style={{
              fontSize: "clamp(1.5rem, 3.2vw, 2.5rem)"
            }}>
                &ldquo;Engineered precision for the modern UAE skyline.&rdquo;
              </blockquote>
              <div className="mt-8 flex items-center gap-4">
                <span aria-hidden="true" className="h-px w-10 bg-secondary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.3em] text-muted-foreground" style={{
                fontFamily: "var(--font-mono)"
              }}>
                  — World Sky Logistics
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}